import type { GenericRecord } from "@domain/models";
import type { GenericRecordTableItem } from "@domain/models/AccountingTableTypes";

/**
 * Returns true if the given tableItem represents a real, non-virtual record row with data -
 * the condition under which column cellRenderers should render themselves. Virtual records
 * (aggregations, group headers) and empty placeholder items should always be skipped.
 */
export const isRenderableTableItem = (tableItem: GenericRecordTableItem | undefined | null): boolean => {
    if (!tableItem) {
        return false;
    }
    if (tableItem.extra?.isVirtualRecord) {
        return false;
    }
    // Inline of isEmptyItem - avoids importing TableGetters (heavy dep chain).
    if (tableItem.key === null || tableItem.key === undefined) {
        return false;
    }
    return true;
};

/**
 * Given displayed rows in their on-screen order (after sort/filter), returns the key of the
 * first *selected* row strictly after `afterKey`. Powers the multi-row "walk-through" edit:
 * once a selected record is saved, a quick follow-up "+" advances the form to the next
 * selected row below it. Returns null when `afterKey` is absent or no selected row follows it.
 */
export const findNextSelectedKey = (rows: { key: string; selected: boolean }[], afterKey: string): string | null => {
    const idx = rows.findIndex((r) => r.key === afterKey);
    if (idx < 0) {
        return null;
    }
    for (let i = idx + 1; i < rows.length; i++) {
        if (rows[i].selected) {
            return rows[i].key;
        }
    }
    return null;
};

/**
 * Walk-through edit: resolves the row to advance to after saving `savedKey`. Engages only for a
 * genuine multi-selection that includes the saved row; "next" comes from the grid's display order
 * (`getNextSelectedKey`) and its record from `tableItems`. Returns null when nothing follows.
 */
export const computeNextAdvance = <R extends { key?: string }>(opts: {
    savedKey: string;
    selectedKeys: Set<string> | undefined;
    tableItems: readonly { item: R }[];
    getNextSelectedKey: (afterKey: string) => string | null | undefined;
}): { key: string; record: R } | null => {
    const { savedKey, selectedKeys } = opts;
    if (!savedKey || !selectedKeys || selectedKeys.size < 2 || !selectedKeys.has(savedKey)) {
        return null;
    }
    const nextKey = opts.getNextSelectedKey(savedKey);
    if (!nextKey) {
        return null;
    }
    const nextItem = opts.tableItems.find((ti) => ti.item.key === nextKey);
    return nextItem ? { key: nextKey, record: nextItem.item } : null;
};

/**
 * Explicit chain-edit: true once the *last* record of a real multi-row run has been saved - i.e.
 * the saved row belongs to a ≥2-row selection but no selected row follows it (`computeNextAdvance`
 * returned null). This is deliberately NOT a bare `!next` check: `computeNextAdvance` also returns
 * null for "not a chain at all" (single record, or a saved row outside the selection - both
 * reachable in chain mode), so we re-apply its multi-selection gates to fire the completion notice
 * only at the genuine end-of-run. Pair with the save-success flag at the call site. (FIBU-901)
 */
export const isChainRunComplete = (opts: {
    next: { key: string } | null;
    selectedKeys: Set<string> | undefined;
    savedKey: string;
}): boolean => {
    const { next, selectedKeys, savedKey } = opts;
    if (next) {
        return false;
    }
    return Boolean(savedKey) && Boolean(selectedKeys) && selectedKeys.size >= 2 && selectedKeys.has(savedKey);
};

/**
 * Returns the base record id, stripping any synthetic "/idx" suffix that
 * KontenAnsicht attaches to reprocessed split-line items.
 *
 * Accepts either a record-like object (anything with a `key`) or a plain id string,
 * so call sites that already hold just the id (context callbacks, row ids, etc.)
 * can use the same helper as those that hold the full record.
 */
export const getRecordId = (input: Pick<GenericRecord, "key"> | string | null | undefined): string => {
    if (!input) {
        return "";
    }
    const key = typeof input === "string" ? input : input.key;
    if (!key) {
        return "";
    }
    return key.includes("/") ? key.split("/")[0] : key;
};

/**
 * AG Grid `isRowSelectable` rule for the accounting tables: pinned and expanded child rows are never
 * selectable; plain data rows defer to the optional per-view predicate (e.g. the DATEV import
 * preview locks error rows and already-imported rows).
 */
export const isSelectableRow = (
    data: (GenericRecordTableItem & { _childKey?: string }) | undefined,
    rowPinned: string | null | undefined,
    custom?: (ti: GenericRecordTableItem) => boolean,
): boolean => {
    if (rowPinned || data?._childKey) {
        return false;
    }
    if (!data || !custom) {
        return true;
    }
    return custom(data);
};
